import React from 'react';
import { NavLink } from '@react-router';
import { useShopStorefrontContext } from '../ShopStorefrontProvider';
import { CatalogStorefront } from '@graphql/generated';
import type { StorefrontNavigatorSpacing, ColorVariant } from './StorefrontNavigator.styles';
import { StyledBadge, StyledNavList, StyledListItem, StyledLink, linkStyles, listItemStyles, newBadgeStyles, navListStyles } from './StorefrontNavigator.styles';
import { BABY_SHOP_PATH, CATALOG_PATH } from '../../Catalog.constants';
import { useRouterHelper, useTranslation } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useCatalogLoggingContext } from '../../Catalog.logging';

type NavListItemProps = {
  storefront: CatalogStorefront;
  onClick: (storefront: CatalogStorefront) => void;
  isActive: boolean;
  isNew?: boolean;
  children: React.ReactNode;
  colorVariant: StorefrontNavigatorProps['colorVariant'];
  spacing: StorefrontNavigatorSpacing;
};

const NewBadge = ({ colorVariant, spacing }: Pick<NavListItemProps, 'colorVariant' | 'spacing'>) => {
  const { t } = useTranslation('catalogRegistry');
  return (
    <StyledBadge
      as="span"
      __css={newBadgeStyles(colorVariant, spacing)}
    >
      {t('storefrontNavigator', 'badgeNew')()}
    </StyledBadge>
  );
};

const NavListItem = (props: NavListItemProps) => {
  const { children, isActive, isNew, onClick, storefront, colorVariant, spacing } = props;
  const { buildPath } = useRouterHelper();
  return (
    <StyledListItem
      as="li"
      __css={listItemStyles}
    >
      <StyledLink
        as={NavLink}
        to={buildPath(storefront === CatalogStorefront.baby ? BABY_SHOP_PATH : CATALOG_PATH)}
        isActive={() => isActive}
        onClick={() => {
          onClick(storefront);
        }}
        __css={linkStyles(colorVariant, spacing)}
      >
        {children}
      </StyledLink>
      {isNew && (
        <NewBadge
          colorVariant={colorVariant}
          spacing={spacing}
        />
      )}
    </StyledListItem>
  );
};

type StorefrontNavigatorProps = {
  spacing?: StorefrontNavigatorSpacing;
  colorVariant: ColorVariant;
};

export const StorefrontNavigator = ({ colorVariant, spacing = 'default' }: StorefrontNavigatorProps) => {
  const { currentStorefront, changeStorefront } = useShopStorefrontContext();
  const { t } = useTranslation('catalogRegistry');
  const { trackShopNavigatorClicked } = useCatalogLoggingContext();

  const handleOnStorefrontClick = useEventCallback((storefront: CatalogStorefront) => {
    trackShopNavigatorClicked({ storefront });
    changeStorefront(storefront);
  });
  return (
    <StyledNavList
      as="ul"
      __css={navListStyles(spacing)}
    >
      <NavListItem
        spacing={spacing}
        colorVariant={colorVariant}
        storefront={CatalogStorefront.wedding}
        isActive={currentStorefront === CatalogStorefront.wedding}
        onClick={handleOnStorefrontClick}
      >
        {t('storefrontNavigator', 'labelStorefrontWedding')()}
      </NavListItem>
      <NavListItem
        spacing={spacing}
        colorVariant={colorVariant}
        isNew={true}
        storefront={CatalogStorefront.baby}
        isActive={currentStorefront === CatalogStorefront.baby}
        onClick={handleOnStorefrontClick}
      >
        {t('storefrontNavigator', 'labelStorefrontBaby')()}
      </NavListItem>
    </StyledNavList>
  );
};
